'use client';

import { ReactNode } from 'react';

import { useSearchContext } from '@/src/services/search/SearchProvider';
import { AdTargetingProvider } from '@nxt-costco-com/forge-digital-components';

/**
 * Bridges the search result's ad targeting flag into the library's AdTargetingProvider.
 * When a search returns FSA/CHD-eligible results, isAdTargetingEnabled becomes false
 * and any ad component consuming useAdTargeting() (e.g. MolocoClient) will suppress itself.
 *
 * Defaults to false when no search result is available yet (initial load),
 * so ads are suppressed until GRS responds and explicitly signals ad targeting is allowed.
 */
export function SearchAdTargetingProvider({
    children,
}: {
    children: ReactNode;
}) {
    const { searchResult, searchedParams, isSearchResultsLoading } =
        useSearchContext();

    // Dev override: set NEXT_PUBLIC_FORCE_AD_TARGETING_ENABLED=true in .env.local to bypass
    // the FSA/CHD gate for local testing when the search API is unavailable (e.g. cert errors).
    const devOverride =
        process.env.NODE_ENV !== 'production' &&
        process.env.NEXT_PUBLIC_FORCE_AD_TARGETING_ENABLED === 'true';

    const isAdTargetingEnabled =
        devOverride ||
        (searchResult !== undefined &&
            (searchResult?.isAdTargetingEnabled ?? true));

    // Convert keyword to array format for AdTargetingProvider
    // (string | string[] | undefined) → (string[] | undefined)
    const searchKeywords = searchedParams?.keyword
        ? Array.isArray(searchedParams.keyword)
            ? searchedParams.keyword
            : [searchedParams.keyword]
        : undefined;

    return (
        <AdTargetingProvider
            isAdTargetingEnabled={isAdTargetingEnabled}
            searchKeywords={searchKeywords}
            isSearchResultsLoading={isSearchResultsLoading}
        >
            {children}
        </AdTargetingProvider>
    );
}
